import { create } from 'zustand';

import { errorMessage } from '@/shared/services/api/api-error';
import { api } from '@/shared/services/api/endpoints';
import { elevation } from '@/shared/services/auth/elevation';

import { useSessionStore } from '@/stores/session.store';

/**
 * Ce que l'écran du code de confidentialité est venu faire.
 *
 *  - `verify` : saisir le code pour passer ;
 *  - `create` : le compte n'en a pas encore, il le choisit ;
 *  - `change` : depuis les réglages, l'ancien puis le nouveau ;
 *  - `reset`  : code oublié, un SMS autorise à en poser un autre.
 */
export type PinFlow = 'verify' | 'create' | 'change' | 'reset';

type PinRequest = {
  flow: PinFlow;
  /** Rend la main à celui qui attendait : `true` si le code est passé. */
  resolve: (granted: boolean) => void;
};

/** Où en est le SMS de réinitialisation. */
type ResetSms = {
  status: 'idle' | 'sending' | 'sent' | 'failed';
  /**
   * Le code, quand le serveur le rend : seulement tant qu'aucune passerelle
   * SMS n'est branchée. L'écran s'en sert alors pour passer l'étape du SMS.
   */
  devCode: string | null;
  error: string | null;
};

const NO_SMS: ResetSms = { status: 'idle', devCode: null, error: null };

interface PinState {
  request: PinRequest | null;
  sms: ResetSms;
  /** Ouvre l'écran du code et attend son issue. */
  open: (flow?: PinFlow) => Promise<boolean>;
  /** Change de parcours sans rendre la main : « code oublié » depuis la saisie. */
  switchTo: (flow: PinFlow) => void;
  close: (granted: boolean) => void;
  /** Envoie, ou renvoie, le SMS qui autorise à poser un nouveau code. */
  sendResetSms: () => Promise<void>;
  /** Le code rendu par le serveur a été refusé : il faut celui du SMS. */
  forgetDevCode: () => void;
}

/**
 * L'écran du code de confidentialité est unique, posé à la racine de
 * l'application : ce store est ce qui permet de l'ouvrir de n'importe où (un
 * écran qui s'ouvre, une requête que le serveur refuse) et d'attendre sa
 * réponse comme on attend une promesse.
 */
export const usePinStore = create<PinState>((set, get) => ({
  request: null,
  sms: NO_SMS,

  open(flow) {
    const pending = get().request;
    if (pending) {
      // Deux demandes en même temps (un écran charge trois requêtes protégées) :
      // la seconde attend l'issue de la première, elle n'ouvre rien de plus.
      return new Promise<boolean>((resolve) => {
        const first = pending.resolve;
        set({
          request: {
            ...pending,
            resolve: (granted) => {
              first(granted);
              resolve(granted);
            },
          },
        });
      });
    }

    const pinSet = useSessionStore.getState().me?.pinSet ?? false;
    const chosen: PinFlow = flow ?? (pinSet ? 'verify' : 'create');
    const opened = new Promise<boolean>((resolve) => {
      set({ request: { flow: chosen, resolve }, sms: NO_SMS });
    });
    if (chosen === 'reset') {
      void get().sendResetSms();
    }
    return opened;
  },

  switchTo(flow) {
    const pending = get().request;
    if (!pending) {
      return;
    }
    set({ request: { ...pending, flow }, sms: NO_SMS });
    if (flow === 'reset') {
      void get().sendResetSms();
    }
  },

  close(granted) {
    const pending = get().request;
    set({ request: null, sms: NO_SMS });
    pending?.resolve(granted);
  },

  async sendResetSms() {
    set({ sms: { status: 'sending', devCode: null, error: null } });
    try {
      const sent = await api.pin.requestReset();
      set({ sms: { status: 'sent', devCode: sent.devCode ?? null, error: null } });
    } catch (failure) {
      set({ sms: { status: 'failed', devCode: null, error: errorMessage(failure) } });
    }
  },

  forgetDevCode() {
    set((state) => ({ sms: { ...state.sms, devCode: null } }));
  },
}));

/**
 * À appeler avant un geste qui touche à l'argent : rend `true` tout de suite si
 * le code vient d'être saisi, sinon ouvre sa saisie (ou sa création, pour un
 * compte qui n'en a pas encore).
 */
export function ensureElevated(): Promise<boolean> {
  const pinSet = useSessionStore.getState().me?.pinSet ?? false;
  if (pinSet && elevation.isFresh()) {
    return Promise.resolve(true);
  }
  return usePinStore.getState().open();
}
